<template>
  <div class="row">
    <div class="col-12">
      <div class="form-group">
        <label class="form-label">Select payment gateway or channel</label>
        <!-- === Dropdown for existing gateways === -->
        <el-dropdown class="w-100" trigger="click">
          <el-input
            class="w-100"
            placeholder="Select payment gateway or channel"
            v-model="selectedGatewayName"
            readonly
          >
            <template #suffix>
              <el-icon><ArrowDown /></el-icon>
            </template>
          </el-input>
          <template #dropdown>
            <el-dropdown-menu class="menu-height">
              <el-dropdown-item
                v-for="(gateway, index) in gateways"
                :key="gateway.id || index"
                @click="selectGateway(gateway)"
              >
                {{
                  gateway.displayName || gateway.gateway?.name || gateway.name
                }}
                <span v-if="gateway.config?.bankName">
                  - {{ gateway.config.bankName }}</span
                >
                <span v-if="gateway.config?.bankAccountNumber">
                  - {{ gateway.config.bankAccountNumber }}</span
                >
                <span v-if="gateway.config?.accountName">
                  - {{ gateway.config.accountName }}</span
                >
              </el-dropdown-item>
              <el-dropdown-item
                class="d-flex justify-content-center cursor-pointer text-primary font-weight-700"
                @click="showChurchPayment = true"
                divided
              >
                <el-icon><CirclePlus /></el-icon> Add new payment gateway
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <div
          @click="showChurchPayment = true"
          class="mt-2 d-flex justify-content-end text-dark fw-300 s-13 cursor-pointer"
        >
          <u> + Add new payment gateway & bank</u>
        </div>
        <!-- Selected Gateways List -->
        <div v-if="internalGateways.length" class="mt-3">
          <div
            v-for="(gateway, index) in internalGateways"
            :key="gateway.id || index"
            class="d-flex justify-content-between align-items-center py-2 border-bottom"
          >
            <span class="s-13">
              {{
                gateway.displayName || gateway.churchPaymentGateway?.displayName
              }}
              <span v-if="gateway.config?.bankName">
                - {{ gateway.config.bankName }}</span
              >
              <span v-if="gateway.config?.bankAccountNumber">
                - {{ gateway.config.bankAccountNumber }}</span
              >
              <span v-if="gateway.config?.accountName">
                - {{ gateway.config.accountName }}</span
              >
            </span>
            <el-icon
              class="cursor-pointer text-danger"
              @click="confirmDelete(gateway)"
            >
              <Delete />
            </el-icon>
          </div>
        </div>
      </div>
      <!-- === Modal for adding new gateway === -->
      <el-dialog
        v-model="showChurchPayment"
        title=""
        :width="mdAndUp || lgAndUp || xlAndUp ? `40%` : `90%`"
        class="border-radius-20"
        @close="resetForm"
      >
        <div class="container-fluid">
          <div class="row">
            <div class="col-md-12 text-center text-dak font-weight-600 s-20">
              ChurchPlus Payment Details
            </div>
            <div class="col-md-12 mt-2 px-0"><hr class="w-100" /></div>
          </div>
          <div class="row mt-3">
            <div class="col-12 mb-3">
              <label class="form-label">Select Payment Gateway Category </label>
              <el-select
                v-model="selectedPaymentForm"
                placeholder="Select payment option"
                class="w-100"
                @change="getFinancialGateway"
              >
                <el-option
                  v-for="form in paymentForms"
                  :key="form.id"
                  :label="form.label"
                  :value="form"
                />
              </el-select>
              <span class="font-italic s-12 fw-500">{{
                selectedPaymentForm?.description
              }}</span>
            </div>
          </div>
          <!-- Payment Gateway -->
          <div
            v-if="
              ['bank', 'connect', 'pub-priv-key'].includes(
                selectedPaymentForm?.value
              )
            "
          >
            <label class="form-label">Select Gateway Payment </label>
            <el-select
              v-model="selectGatewayPaymentID"
              placeholder="Select Payment Gateway"
              class="w-100 mb-4"
              @change="setSelectedGatewayPayment"
            >
              <el-option
                v-for="item in gateWayPayments"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
          <!-- Bank Form -->
          <div v-if="selectedPaymentForm?.value === 'bank'">
            <div>
              <label class="form-label">Choose Bank</label>
              <el-select
                v-model="selectedBankID"
                placeholder="Select Bank"
                filterable
                class="w-100 mb-3"
                @change="setSelectedBank"
              >
                <el-option
                  v-for="bank in filteredBanks"
                  :key="bank.id"
                  :label="bank.name"
                  :value="bank.id"
                />
              </el-select>
            </div>
            <div>
              <label class="form-label">Account Number</label>
              <el-input
                v-model="accountNumber"
                placeholder="Account Number"
                class="mb-3"
                @blur="resolveCustomerDetail"
              />
              <span v-if="loading">
                <el-icon :size="20" class="is-loading"> <Loading /> </el-icon>
              </span>
            </div>
            <div>
              <label class="form-label">Account Name</label>
              <el-input
                v-model="accountName"
                placeholder="Account Name"
                disabled
                class="mb-3"
              />
              <small class="helper-text">This will automatically come up</small>
            </div>
          </div>
          <!-- Connect Form -->
          <div v-if="selectedPaymentForm?.value === 'connect'">
            <div>
              <label class="form-label">External Account Ref</label>
              <el-input
                v-model="connectForm.externalAccountRef"
                placeholder="External Account Ref"
                class="mb-3"
              />
            </div>
            <div>
              <label class="form-label">Access Token</label>
              <el-input
                v-model="connectForm.accessTokenEncrypted"
                placeholder="Access Token"
                class="mb-3"
              />
            </div>
            <div>
              <label class="form-label">Refresh Token</label>
              <el-input
                v-model="connectForm.refreshTokenEncrypted"
                placeholder="Refresh Token"
                class="mb-3"
              />
            </div>
            <div>
              <label class="form-label">Connected At</label>
              <el-date-picker
                v-model="connectForm.connectedAt"
                type="datetime"
                placeholder="Connected At"
                class="w-100 mb-3"
                size="large"
              />
            </div>
          </div>
          <!-- Key Form -->
          <div v-if="selectedPaymentForm?.value === 'pub-priv-key'">
            <div>
              <label class="form-label">Public Key</label>
              <el-input
                v-model="keysForm.publicKeyEncrypted"
                placeholder="Public Key"
                class="mb-3"
              />
            </div>
            <div>
              <label class="form-label">Secret Key</label>
              <el-input
                v-model="keysForm.secretKeyEncrypted"
                placeholder="Secret Key"
                class="mb-3"
              />
            </div>
            <div>
              <label class="form-label">Webhook Key</label>
              <el-input
                v-model="keysForm.webhookSecretEncrypted"
                placeholder="Webhook Secret"
                class="mb-3"
              />
            </div>
          </div>
          <div
            class="mt-4"
            v-if="
              ['bank', 'connect', 'pub-priv-key'].includes(
                selectedPaymentForm?.value
              )
            "
          >
            <el-button
              type="primary"
              round
              class="w-100 py-4 text-white"
              :loading="loading"
              :color="primarycolor"
              @click="saveGateway"
            >
              Save Gateway
            </el-button>
          </div>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, inject } from "vue";
import { ElMessageBox, ElNotification } from "element-plus";
import axios from "@/gateway/backendapi";
import axio from "axios";
import deviceBreakpoint from "../../mixins/deviceBreakpoint";
import isEqual from "lodash/isEqual";

// props
const props = defineProps({
  selectedGateways: {
    type: Array,
    default: () => [],
  },
});

// emits
const emit = defineEmits(["update:selectedGateways", "selectedGateways"]);

// UI
const primarycolor = inject("primarycolor");
const { lgAndUp, xlAndUp, mdAndUp } = deviceBreakpoint();

// state
const showChurchPayment = ref(false);
const loading = ref(false);
const selectedGatewayName = ref("");
const gateways = ref([]);
const gateWayPayments = ref([]);
const nigerianBanks = ref([]);

const selectedPaymentForm = ref(null);
const selectedBankID = ref(null);
const selectGatewayPaymentID = ref(null);

const selectedPaymentGateway = ref({});
const selectedBank = ref({});

const accountNumber = ref("");
const accountName = ref("");
const paymentForms = ref([
  {
    id: 0,
    label: "ChurchPlus Gateway – Quick Start (Recommended)",
    value: "bank",
    description:
      "No setup required, fastest to start. ChurchPlus handles settlement, payouts, and compliance.",
  },
  {
    id: 1,
    label: "Own Gateway – Easy Setup (Linked Account / Connect)",
    value: "connect",
    description:
      "The church connects their Stripe, Paystack, or other supported platforms via OAuth or dashboard link.",
  },
  {
    id: 2,
    label: "Custom Gateway – Use API Keys",
    value: "pub-priv-key",
    description:
      "The most advanced option where the church pastes API keys. Best for tech-savvy teams or larger churches.",
  },
]);

// forms
const connectForm = reactive({
  externalAccountRef: "",
  accessTokenEncrypted: "",
  refreshTokenEncrypted: "",
  connectedAt: new Date().toISOString(),
});
const keysForm = reactive({
  publicKeyEncrypted: "",
  secretKeyEncrypted: "",
  webhookSecretEncrypted: "",
});

// internal list
const internalGateways = ref([]);

internalGateways.value = [...props.selectedGateways];

watch(
  () => props.selectedGateways,
  (newVal) => {
    if (!isEqual(newVal, internalGateways.value)) {
      internalGateways.value = [...newVal];
    }
  },
  { deep: true, immediate: true }
);

// Watch internal -> parent (only if different)
watch(
  internalGateways,
  (newVal) => {
    if (!isEqual(newVal, props.selectedGateways)) {
      emit("update:selectedGateways", newVal);
      emit("selectedGateways", newVal);
    }
  },
  { deep: true }
);

// computed
const filteredBanks = computed(() => nigerianBanks.value);

// load data
const getGateways = async () => {
  try {
    const { data } = await axios.get("/api/Payment/churchpaymentgateway");
    gateways.value = data;
  } catch (err) {
    console.error(err);
  }
};
getGateways();

const getBanks = async () => {
  try {
    const { data } = await axios.get("/api/Financials/GetBanks");
    nigerianBanks.value = data;
  } catch (err) {
    console.error(err);
  }
};
getBanks();

const getFinancialGateway = async () => {
  try {
    const { data } = await axios.get(
      "/api/Payment/GetPaymentGatewaysByIntegrationType",
      {
        params: { integrationType: selectedPaymentForm.value.id },
      }
    );
    gateWayPayments.value = data;
  } catch (err) {
    console.error(err);
  }
};

// resolve account name
const resolveCustomerDetail = async () => {
  if (!accountNumber.value || !selectedBank.value) return;

  loading.value = true;
  try {
    const res = await axio.post(
      "https://api.ravepay.co/flwv3-pug/getpaidx/api/resolve_account",
      {
        recipientaccount: accountNumber.value,
        destbankcode: selectedBank.value.code,
        PBFPubKey: process.env.VUE_APP_FLUTTERWAVE_PUBLIC_KEY_LIVE,
      }
    );

    accountName.value = res.data?.data?.data?.accountname || "";
  } catch (err) {
    ElNotification.error("Could not resolve account name");
  } finally {
    loading.value = false;
  }
};

// setters
const setSelectedBank = () => {
  selectedBank.value =
    nigerianBanks.value.find((b) => b.id === selectedBankID.value) || {};
};

const setSelectedGatewayPayment = () => {
  selectedPaymentGateway.value =
    gateWayPayments.value.find((g) => g.id === selectGatewayPaymentID.value) ||
    {};
};

// SAVE gateway
const saveGateway = async () => {
  loading.value = true;
  try {
    let config = {};

    if (selectedPaymentForm.value.value === "bank") {
      config = {
        bankName: selectedBank.value.name,
        bankCode: selectedBank.value.code,
        bankAccountNumber: accountNumber.value,
        accountName: accountName.value,
      };
    } else if (selectedPaymentForm.value.value === "connect") {
      config = { ...connectForm };
    } else {
      config = { ...keysForm };
    }

    const payload = {
      paymentGatewayID: selectedPaymentGateway.value.id,
      displayName: selectedPaymentGateway.value.name || "Custom Gateway",
      config,
      createdAt: new Date().toISOString(),
    };

    const res = await axios.post("/api/Payment/churchpaymentgateway", payload);

    await getGateways(); // reload fresh list

    const newest = gateways.value[gateways.value.length - 1];

    // add without duplicates
    if (!internalGateways.value.some((g) => g.id === newest.id)) {
      internalGateways.value.push(newest);
    }

    ElNotification.success("Gateway saved successfully");
    showChurchPayment.value = false;
    resetForm();
  } catch (err) {
    console.error(err);
    ElNotification.error("Error saving gateway");
  } finally {
    loading.value = false;
  }
};

// select existing gateway
// const selectGateway = (gateway) => {
//   selectedGatewayName.value = gateway.displayName || gateway.name || "";

//   if (!internalGateways.value.some((g) => g.id === gateway.id)) {
//     internalGateways.value.push(gateway);
//   }
// };
const selectGateway = (gateway) => {
  // Set the selected gateway name
  selectedGatewayName.value =
    gateway.displayName || gateway.gateway?.name || gateway.name || "";

  // Check if the gateway is already in the list
  const exists = internalGateways.value.some((g) => g.id === gateway.id);

  if (!exists) {
    // Add gateway if not exists
    internalGateways.value.push(gateway);

    // Show success notification
    ElNotification({
      title: "Gateway Selected",
      message: `${gateway.displayName || gateway.name} was added.`,
      type: "success",
    });
  } else {
    // Show warning if already added
    ElNotification({
      title: "Already Added",
      message: `${gateway.displayName || gateway.name} is already in the list.`,
      type: "warning",
    });
  }
};

// delete
const confirmDelete = (gateway) => {
  ElMessageBox.confirm(
    "Are you sure you want to delete this gateway?",
    "Confirm delete",
    {
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      type: "error",
    }
  )
    .then(() => {
      internalGateways.value = internalGateways.value.filter(
        (g) => g.id !== gateway.id
      );
      ElNotification.success("Gateway removed");
    })
    .catch(() => {});
};

// reset
const resetForm = () => {
  selectedPaymentForm.value = null;
  selectedBankID.value = null;
  selectGatewayPaymentID.value = null;

  accountNumber.value = "";
  accountName.value = "";

  Object.assign(connectForm, {
    externalAccountRef: "",
    accessTokenEncrypted: "",
    refreshTokenEncrypted: "",
    connectedAt: "",
  });

  Object.assign(keysForm, {
    publicKeyEncrypted: "",
    secretKeyEncrypted: "",
    webhookSecretEncrypted: "",
  });
};
</script>

<style scoped>
.form-label {
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}
.row-button {
  padding: 15px;
  border-bottom: 1px solid #bfd7ff;
  transition: all 0.4s ease-in-out;
  max-height: 70px;
}
.img-pay {
  width: 125px;
}
.gradient-text {
  background: linear-gradient(90deg, #671414 0%, #0b55d5 100%);
  background-clip: text;
  color: transparent;
}
.card-bg {
  background: #fafafa;
}
.subb {
  background: #fafafa;
  border: 1px solid #d3d3d3;
  border-radius: 8px;
}
</style>
